import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminProductsResponse,
  AdminTeam,
  AdminUsersResponse,
  AuditResponse,
  JiraBoardOption,
  JiraMappingRow,
  MondayBoardOption,
  JiraPerson,
  ProductJiraFieldsResponse,
  ProductJiraFieldsSaveInput,
  ProductSaveInput,
  TeamSaveInput,
  UserInviteInput,
} from '@product-portal/shared';
import { apiFetch } from '@/lib/api';

/** Data of the administration (10, 11, 12, 14). PO only, enforced by the API; no role is ever written. */

const adminKeys = {
  all: ['admin'] as const,
  teams: ['admin', 'teams'] as const,
  users: (params: object) => ['admin', 'users', params] as const,
  products: ['admin', 'products'] as const,
  boards: ['admin', 'jira-boards'] as const,
  boardStatuses: (boardId: number | null) => ['admin', 'jira-board-statuses', boardId] as const,
  audit: (params: object) => ['admin', 'audit', params] as const,
};

const json = (body: unknown) => JSON.stringify(body);

function useAdminMutation<TVariables, TResult>(fn: (variables: TVariables) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // Teams, users and products feed every other screen (forms, filters): refresh everything.
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useAdminTeams() {
  return useQuery({ queryKey: adminKeys.teams, queryFn: () => apiFetch<{ items: AdminTeam[] }>('/api/admin/teams').then((r) => r.items) });
}

export function useSaveTeam() {
  return useAdminMutation(({ id, input }: { id: string | null; input: TeamSaveInput }) =>
    apiFetch<{ id: string }>(id ? `/api/admin/teams/${id}` : '/api/admin/teams', { method: id ? 'PUT' : 'POST', body: json(input) }),
  );
}

export interface UsersParams {
  q: string;
  role: string;
  teamId: string;
  page: number;
}

export function useAdminUsers(params: UsersParams) {
  const search = new URLSearchParams({ page: String(params.page), pageSize: '20' });
  if (params.q.trim()) search.set('q', params.q.trim());
  if (params.role) search.set('role', params.role);
  if (params.teamId) search.set('teamId', params.teamId);
  return useQuery({
    queryKey: adminKeys.users(params),
    queryFn: () => apiFetch<AdminUsersResponse>(`/api/admin/users?${search.toString()}`),
    placeholderData: keepPreviousData,
  });
}

/** « Créer un nouvel utilisateur » (ADR-014): account, teams and invitation email. */
export function useInviteUser() {
  return useAdminMutation((input: UserInviteInput) => apiFetch<{ id: string; emailSent: boolean }>('/api/admin/users', { method: 'POST', body: json(input) }));
}

export function useAttach() {
  return useAdminMutation(({ userId, teamId }: { userId: string; teamId: string }) =>
    apiFetch(`/api/admin/users/${userId}/teams`, { method: 'POST', body: json({ teamId }) }),
  );
}

export function useDetach() {
  return useAdminMutation(({ userId, teamId }: { userId: string; teamId: string }) => apiFetch(`/api/admin/users/${userId}/teams/${teamId}`, { method: 'DELETE' }));
}

export function useAdminProducts() {
  return useQuery({ queryKey: adminKeys.products, queryFn: () => apiFetch<AdminProductsResponse>('/api/admin/products') });
}

export function useSaveProduct() {
  return useAdminMutation(({ id, input }: { id: string | null; input: ProductSaveInput }) =>
    apiFetch<{ id: string }>(id ? `/api/admin/products/${id}` : '/api/admin/products', { method: id ? 'PUT' : 'POST', body: json(input) }),
  );
}

export function useSaveMapping() {
  return useAdminMutation(({ productId, tracker = 'jira', mappings }: { productId: string; tracker?: 'jira' | 'monday'; mappings: JiraMappingRow[] }) =>
    apiFetch(`/api/admin/products/${productId}/${tracker}-statuses`, { method: 'PUT', body: json({ mappings }) }),
  );
}

/** Required fields of the product's Jira project and the values sent (ADR-013). Reads Jira: no retry. */
export function useProductJiraFields(productId: string) {
  return useQuery({
    queryKey: ['admin', 'jira-fields', productId] as const,
    queryFn: () => apiFetch<ProductJiraFieldsResponse>(`/api/admin/products/${productId}/jira-fields`),
    retry: false,
  });
}

export function useSaveProductJiraFields(productId: string) {
  return useAdminMutation((input: ProductJiraFieldsSaveInput) => apiFetch(`/api/admin/products/${productId}/jira-fields`, { method: 'PUT', body: json(input) }));
}

export function useJiraUsers(q: string) {
  return useQuery({
    queryKey: ['admin', 'jira-users', q.trim()] as const,
    queryFn: () => apiFetch<{ items: JiraPerson[] }>(`/api/admin/jira/users?${new URLSearchParams({ q: q.trim() })}`).then((r) => r.items),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });
}

/** Monday boards with their status columns (ADR-015). */
export function useMondayBoards(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'monday-boards'] as const,
    queryFn: () => apiFetch<{ items: MondayBoardOption[] }>('/api/admin/monday/boards').then((r) => r.items),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useMondayStatuses(boardId: string, columnId: string) {
  return useQuery({
    queryKey: ['admin', 'monday-statuses', boardId, columnId] as const,
    queryFn: () => apiFetch<{ items: string[] }>(`/api/admin/monday/boards/${boardId}/columns/${columnId}/statuses`).then((r) => r.items),
    enabled: boardId !== '' && columnId !== '',
    retry: false,
  });
}

export function useJiraBoards(enabled: boolean) {
  return useQuery({
    queryKey: adminKeys.boards,
    queryFn: () => apiFetch<{ items: JiraBoardOption[] }>('/api/admin/jira/boards').then((r) => r.items),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useBoardStatuses(boardId: number | null) {
  return useQuery({
    queryKey: adminKeys.boardStatuses(boardId),
    queryFn: () => apiFetch<{ items: string[] }>(`/api/admin/jira/boards/${boardId}/statuses`).then((r) => r.items),
    enabled: boardId !== null,
    retry: false,
  });
}

export interface AuditParams {
  actorId: string;
  group: string;
  period: string;
  /** « Personnalisée »: YYYY-MM-DD bounds, each optional. */
  from: string;
  to: string;
  q: string;
  page: number;
}

export function auditSearch(params: AuditParams): URLSearchParams {
  const search = new URLSearchParams({ period: params.period, page: String(params.page) });
  if (params.actorId) search.set('actorId', params.actorId);
  if (params.group) search.set('group', params.group);
  if (params.q.trim()) search.set('q', params.q.trim());
  if (params.period === 'custom' && params.from) search.set('from', params.from);
  if (params.period === 'custom' && params.to) search.set('to', params.to);
  return search;
}

export function useAuditLog(params: AuditParams) {
  return useQuery({
    queryKey: adminKeys.audit(params),
    queryFn: () => apiFetch<AuditResponse>(`/api/admin/audit?${auditSearch(params).toString()}`),
    placeholderData: keepPreviousData,
  });
}
